// DemoData.ts
// -----------------------------------------------------------------------------
// Static, always-identical data for the demo session, embedded directly in the
// FE. The ONLY thing computed at runtime is timestamps: both the chart trend and
// each monitor's `run_execution_time` are anchored to the current system clock so
// the data is always "now" and never slides off the visible chart window.
// -----------------------------------------------------------------------------

import {
  ServiceStatus,
  TrendData,
  MonitorGroup,
  FavoriteService,
  UserProfile,
  TagConfig,
} from "../Models/Types";
import { DEMO_USER_EMAIL } from "./DemoSession";

// ---------- helpers ----------------------------------------------------------

/** now, floored to the nearest 5-minute slot (keeps chart ticks tidy). */
const flooredNow = (stepMs = 5 * 60 * 1000): number =>
  Math.floor(Date.now() / stepMs) * stepMs;

// -----------------------------------------------------------------------------
// APPLICATIONS + MONITORS (static)
// Each row is one monitor; several monitors share an application_id. This is the
// single dataset that drives the list, aggregations, groups, favorites and KSP.
// `run_execution_time` is filled in at fetch time (see buildStatus()).
// -----------------------------------------------------------------------------

interface StaticMonitor {
  monitor_service_id: number;
  monitor_name: string;
  application_id: number;
  application: string;
  application_description: string;
  responsible: string;
  developer: string;
  classification: string;
  kpi: boolean;
  run_status: boolean;
  duration_secs: number;
  application_tags: string[];
  // minutes-ago the monitor last ran (turned into a live timestamp at fetch time)
  ran_minutes_ago: number;
}

const STATIC_MONITORS: StaticMonitor[] = [
  // --- 813 MRRIUSO (OK, kpi) ---
  { monitor_service_id: 2229, monitor_name: "Motore di ricerca SMARTDATANET", application_id: 813, application: "MRRIUSO_PROD_RP-01", application_description: "Motore di indexer e search per open data", responsible: "MARIO ROSSI", developer: "GIUSEPPE VERDI", classification: "Backend Service", kpi: true, run_status: true, duration_secs: 578, application_tags: ["opendata"], ran_minutes_ago: 3 },
  { monitor_service_id: 2230, monitor_name: "Health check indexer", application_id: 813, application: "MRRIUSO_PROD_RP-01", application_description: "Motore di indexer e search per open data", responsible: "MARIO ROSSI", developer: "GIUSEPPE VERDI", classification: "Backend Service", kpi: false, run_status: true, duration_secs: 121, application_tags: ["opendata"], ran_minutes_ago: 3 },

  // --- 919 WEBHOSTING (OK) ---
  { monitor_service_id: 2573, monitor_name: "Portale Arianna", application_id: 919, application: "WEBHOSTING_PROD_COTO-01", application_description: "Application dedicata per il servizio di webhosting", responsible: "MARIO ROSSI", developer: "GIUSEPPE VERDI", classification: "Web Application", kpi: false, run_status: true, duration_secs: 44, application_tags: ["web"], ran_minutes_ago: 5 },

  // --- 953 PAGOPA (KO, kpi) ---
  { monitor_service_id: 3101, monitor_name: "Pagamenti PagoPA - checkout", application_id: 953, application: "PAGOPA_PROD_PA-02", application_description: "Piattaforma pagamenti elettronici", responsible: "LAURA BIANCHI", developer: "ANNA NERI", classification: "Payment Gateway", kpi: true, run_status: false, duration_secs: 1820, application_tags: ["pagamenti", "kpi"], ran_minutes_ago: 2 },
  { monitor_service_id: 3102, monitor_name: "Riconciliazione flussi", application_id: 953, application: "PAGOPA_PROD_PA-02", application_description: "Piattaforma pagamenti elettronici", responsible: "LAURA BIANCHI", developer: "ANNA NERI", classification: "Payment Gateway", kpi: false, run_status: true, duration_secs: 610, application_tags: ["pagamenti"], ran_minutes_ago: 7 },

  // --- 1108 SPID (OK, kpi) ---
  { monitor_service_id: 3310, monitor_name: "Autenticazione SPID", application_id: 1108, application: "IDENTITY_PROD_SPID-01", application_description: "Federazione identità digitale", responsible: "PAOLO GIALLI", developer: "SARA VIOLA", classification: "Identity Provider", kpi: true, run_status: true, duration_secs: 305, application_tags: ["identity", "kpi"], ran_minutes_ago: 1 },

  // --- 1137 GEOPORTALE (KO) ---
  { monitor_service_id: 3450, monitor_name: "WMS tile server", application_id: 1137, application: "GEO_PROD_MAP-04", application_description: "Geoportale regionale cartografia", responsible: "PAOLO GIALLI", developer: "LUCA ROSSO", classification: "GIS Service", kpi: false, run_status: false, duration_secs: 2540, application_tags: ["gis"], ran_minutes_ago: 4 },
  { monitor_service_id: 3451, monitor_name: "Catalogo metadati", application_id: 1137, application: "GEO_PROD_MAP-04", application_description: "Geoportale regionale cartografia", responsible: "PAOLO GIALLI", developer: "LUCA ROSSO", classification: "GIS Service", kpi: false, run_status: true, duration_secs: 190, application_tags: ["gis"], ran_minutes_ago: 6 },

  // --- 43 ANAGRAFE (OK) ---
  { monitor_service_id: 1201, monitor_name: "Consultazione anagrafica", application_id: 43, application: "ANPR_PROD_REG-01", application_description: "Anagrafe nazionale popolazione residente", responsible: "GIOVANNA CONTI", developer: "MARCO FERRI", classification: "Backend Service", kpi: false, run_status: true, duration_secs: 88, application_tags: ["anagrafe"], ran_minutes_ago: 8 },

  // --- 971 FATTURAZIONE (OK) ---
  { monitor_service_id: 2800, monitor_name: "Fatturazione elettronica", application_id: 971, application: "FATTURE_PROD_FE-01", application_description: "Ciclo passivo fatturazione elettronica", responsible: "GIOVANNA CONTI", developer: "MARCO FERRI", classification: "Batch", kpi: false, run_status: true, duration_secs: 940, application_tags: ["fatture"], ran_minutes_ago: 11 },

  // --- 635 PROTOCOLLO (KO) ---
  { monitor_service_id: 2410, monitor_name: "Protocollo informatico", application_id: 635, application: "PROTO_PROD_DOC-02", application_description: "Sistema di protocollo documentale", responsible: "ELENA MORO", developer: "DAVIDE SANTI", classification: "Document Management", kpi: false, run_status: false, duration_secs: 3120, application_tags: ["documentale"], ran_minutes_ago: 9 },

  // --- 577 POSTA (OK) ---
  { monitor_service_id: 2205, monitor_name: "Gateway PEC", application_id: 577, application: "PEC_PROD_MAIL-01", application_description: "Posta elettronica certificata", responsible: "ELENA MORO", developer: "DAVIDE SANTI", classification: "Mail Service", kpi: false, run_status: true, duration_secs: 76, application_tags: ["mail"], ran_minutes_ago: 13 },

  // --- 159 SANITA (OK, kpi) ---
  { monitor_service_id: 1900, monitor_name: "Fascicolo Sanitario Elettronico", application_id: 159, application: "FSE_PROD_HEALTH-01", application_description: "Fascicolo sanitario elettronico regionale", responsible: "STEFANO RICCI", developer: "CHIARA LUPO", classification: "Health Service", kpi: true, run_status: true, duration_secs: 420, application_tags: ["sanita", "kpi"], ran_minutes_ago: 5 },

  // --- 825 PRENOTAZIONI (OK) ---
  { monitor_service_id: 1955, monitor_name: "CUP prenotazioni", application_id: 825, application: "CUP_PROD_BOOK-03", application_description: "Centro unico prenotazioni sanitarie", responsible: "STEFANO RICCI", developer: "CHIARA LUPO", classification: "Booking Service", kpi: false, run_status: true, duration_secs: 260, application_tags: ["sanita"], ran_minutes_ago: 12 },

  // --- 463 TRIBUTI (KO) ---
  { monitor_service_id: 2010, monitor_name: "Calcolo tributi", application_id: 463, application: "TRIB_PROD_TAX-01", application_description: "Motore di calcolo tributi locali", responsible: "ROBERTO MELE", developer: "FEDERICA GALLO", classification: "Batch", kpi: false, run_status: false, duration_secs: 4010, application_tags: ["tributi"], ran_minutes_ago: 10 },

  // --- 108 SUAP (OK) ---
  { monitor_service_id: 2110, monitor_name: "Sportello unico attività produttive", application_id: 108, application: "SUAP_PROD_BIZ-01", application_description: "Pratiche SUAP telematiche", responsible: "ROBERTO MELE", developer: "FEDERICA GALLO", classification: "Web Application", kpi: false, run_status: true, duration_secs: 150, application_tags: ["imprese"], ran_minutes_ago: 15 },

  // --- 52 TRASPORTI (OK) ---
  { monitor_service_id: 1500, monitor_name: "Orari trasporto pubblico", application_id: 52, application: "TPL_PROD_TRANSIT-01", application_description: "Trasporto pubblico locale - orari real time", responsible: "SILVIA COSTA", developer: "ANDREA PACE", classification: "API", kpi: false, run_status: true, duration_secs: 64, application_tags: ["trasporti"], ran_minutes_ago: 6 },
];

/**
 * Returns the full ServiceStatus[] with every monitor's `run_execution_time`
 * stamped to "a few minutes ago" relative to the current clock, so the list and
 * the "last execution" sorting always look fresh.
 */
export const buildStatus = (): ServiceStatus[] => {
  const nowMs = Date.now();
  return STATIC_MONITORS.map((m) => ({
    monitor_service_id: m.monitor_service_id,
    monitor_name: m.monitor_name,
    monitor_description: m.monitor_name,
    application_id: m.application_id,
    application: m.application,
    application_description: m.application_description,
    responsible: m.responsible,
    developer: m.developer,
    organization: null,
    division: null,
    account: null,
    classification: m.classification,
    kpi: m.kpi,
    public_data: null,
    run_execution_time: new Date(nowMs - m.ran_minutes_ago * 60 * 1000).toISOString(),
    run_elapsed_time: String(m.duration_secs),
    run_status: m.run_status,
    run_location_id: 10,
    run_external: false,
    bo_active: false,
    duration_secs: m.duration_secs,
    application_tags: m.application_tags,
  }));
};

// -----------------------------------------------------------------------------
// TREND (static shape, live timestamps)
// 24h of 5-minute points. Values are a fixed, deterministic function of "how many
// slots ago" a point is, so the curve shape is stable across renders and the most
// recent point is always the same value — the chart never jumps or slides away.
// -----------------------------------------------------------------------------

const TREND_HOURS = 24;
const TREND_STEP_MS = 5 * 60 * 1000;
const TREND_POINTS = (TREND_HOURS * 60) / 5; // 288

/** Deterministic OK/KO/BO for a point `iFromEnd` slots before "now". */
const trendSlot = (iFromEnd: number): { ok: number; ko: number; bo: number } => {
  // gentle daily wave for OK volume
  const wave = Math.sin((iFromEnd / TREND_POINTS) * Math.PI * 2);
  const ok = Math.round(11 + wave * 3 + (iFromEnd % 4)); // ~8..17
  // KO spikes occasionally but is 0 most of the time
  const ko = iFromEnd % 23 === 0 ? 3 : iFromEnd % 11 === 0 ? 1 : 0;
  const bo = iFromEnd % 37 === 0 ? 1 : 0;
  return { ok: Math.max(0, ok), ko, bo };
};

export const buildTrend = (): TrendData => {
  const end = flooredNow(TREND_STEP_MS);

  const monitor_count: Record<string, number> = {};
  const monitor_ok: Record<string, number> = {};
  const monitor_ko: Record<string, number> = {};
  const monitor_bo: Record<string, number> = {};

  // Emit oldest -> newest so keys are naturally ordered; the chart sorts anyway.
  for (let iFromEnd = TREND_POINTS - 1; iFromEnd >= 0; iFromEnd--) {
    const tsMs = end - iFromEnd * TREND_STEP_MS;
    const key = new Date(tsMs).toISOString();
    const { ok, ko, bo } = trendSlot(iFromEnd);
    monitor_ok[key] = ok;
    monitor_ko[key] = ko;
    monitor_bo[key] = bo;
    monitor_count[key] = ok + ko + bo;
  }

  return { monitor_count, monitor_ok, monitor_ko, monitor_bo };
};

// -----------------------------------------------------------------------------
// SEED DATA for the mocked "backend" (persisted per-session in localStorage).
// -----------------------------------------------------------------------------

export const SEED_FAVORITES: FavoriteService[] = [
  { applicationId: 953, ownerName: DEMO_USER_EMAIL },
  { applicationId: 1108, ownerName: DEMO_USER_EMAIL },
];

export const SEED_GROUPS: MonitorGroup[] = [
  {
    id: 1,
    groupName: "Servizi critici",
    ownerName: DEMO_USER_EMAIL,
    applicationIdsInGroup: "953,1108,1137",
    threshold: 10,
  },
  {
    id: 2,
    groupName: "Sanità",
    ownerName: DEMO_USER_EMAIL,
    applicationIdsInGroup: "159,825",
    threshold: 20,
  },
];

export const SEED_USERS: UserProfile[] = [
  { email: DEMO_USER_EMAIL, role: "admin", tokens: "0", enabled: true, allow_notifications: false },
  { email: "operatore@demo.local", role: "user", tokens: "0", enabled: true, allow_notifications: true },
  { email: "sospeso@demo.local", role: "user", tokens: "0", enabled: false, allow_notifications: false },
];

export const SEED_TAGS: TagConfig[] = [
  { tag_name: "pagamenti", threshold: 5, pause_minutes: 15 },
  { tag_name: "sanita", threshold: 8, pause_minutes: 30 },
  { tag_name: "gis", threshold: 15, pause_minutes: 60 },
];

export const SEED_SETTINGS = {
  globalThreshold: 10,
  notificationPause: 30,
  groupNotificationPause: 60,
};